// Block picker modal — opens from the Template editor's "+ Add block"
// CTA. Per spec/build-plan.md P-031 row decision surface ("Block picker
// UX — modal for v1") + Banks-confirmed at P-031 pre-flight.
//
// **Built on `<Modal>` (P-032).** Per /simplify at P-032 close — the
// 3rd modal-scaffold consumer (NewEmailModal) triggered the shared
// extraction. This component layers the title + Close button +
// category-grouped tile grid on top of the shell.
//
// **Tile content = label + id.** Mirrors `<LibraryScreen>`'s per-Block
// summary surface. Category headings group the 15 Blocks per
// `BlockDefinition.category` (header / content / footer / meta).

import { useEffect, useRef } from 'preact/hooks'

import type { BlockCategory } from '../../types'
import { blocks } from '../../registry/blocks'
import { secondaryButtonClassCompactSm } from '../button-classes'
import { Modal } from '../modal'

interface BlockPickerModalProps {
  readonly open: boolean
  readonly onPick: (id: string) => void
  readonly onCancel: () => void
}

const CATEGORY_ORDER: readonly BlockCategory[] = ['header', 'content', 'footer', 'meta']

function categoryLabel(c: BlockCategory): string {
  return c[0]!.toUpperCase() + c.slice(1)
}

interface BlockTile {
  readonly id: string
  readonly label: string
}

interface CategoryGroup {
  readonly category: BlockCategory
  readonly items: readonly BlockTile[]
}

const GROUPED: readonly CategoryGroup[] = CATEGORY_ORDER.map((category) => ({
  category,
  items: Object.values(blocks)
    .filter((def) => def.category === category)
    .map((def) => ({ id: def.id, label: def.label })),
}))

export function BlockPickerModal({ open, onPick, onCancel }: BlockPickerModalProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!open) return
    closeRef.current?.focus()
  }, [open])

  return (
    <Modal open={open} onCancel={onCancel} labelledBy="block-picker-modal-title" size="lg">
      <div class="flex items-baseline justify-between gap-4">
        <h2 id="block-picker-modal-title" class="m-0 text-lg font-semibold text-gray-900">
          Add a block
        </h2>
        <button
          ref={closeRef}
          type="button"
          onClick={onCancel}
          class={secondaryButtonClassCompactSm}
        >
          Close
        </button>
      </div>
      <div class="mt-4 space-y-6">
        {GROUPED.map(({ category, items }) => (
          <section key={category} aria-labelledby={`block-picker-cat-${category}`}>
            <h3
              id={`block-picker-cat-${category}`}
              class="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              {categoryLabel(category)}
            </h3>
            <ul class="m-0 grid list-none grid-cols-1 gap-2 p-0 sm:grid-cols-2">
              {items.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => onPick(b.id)}
                    class="w-full rounded border border-gray-200 bg-white px-3 py-2 text-left hover:border-gray-300 hover:bg-gray-50"
                  >
                    <div class="text-sm font-medium text-gray-900">{b.label}</div>
                    <code class="text-xs text-gray-500">{b.id}</code>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  )
}
